import styles from "./styles.module.scss";
import { useState, useEffect, useRef } from "react";
import { EmptyTable } from "components/empty-table";
import { ChevronRightIcon, CloseCircleGreyIcon, DocumentDownload, ExportIcon, emptyReport2, FilterSearchIcon, SearchIconTwo, CloseIcon } from "assets";
import { Button } from "components/button";
import { Modal } from "components/modal";
import { AssessmentTable, DropdownMenu, DropdownMenuOption, Input, RangeSlider } from "components";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { Routes } from "router";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useReviewTalentsCandidates, useUpdateCandidateDecision, CandidateDecision, SortByField, useFetchDecisionBuckets, useCandidateRetake, useExportCandidates, useDownloadCVs, getMetricKeys, getMetricScore, formatMetricLabel } from "hooks";
import { Spinner } from "components/loader";
import * as Popover from "@radix-ui/react-popover";

const EXPORT_COLUMNS = [
  { label: "Name", value: "name" },
  { label: "Date", value: "date" },
  { label: "Email address", value: "email" },
  { label: "Phone number", value: "phone_number" },
  { label: "CV", value: "cv" },
  { label: "Portfolio link", value: "portfolio_link" },
  { label: "Score", value: "score" },
  { label: "Decision", value: "decision" },
];

// Options exposed by the toolbar sort dropdown. Each maps to the backend's
// sort_by / sort_order params on /candidate/dashboard/list/.
const SORT_OPTIONS: { label: string; sortBy: SortByField; sortOrder: "asc" | "desc" }[] = [
  { label: "Latest", sortBy: "created_at", sortOrder: "desc" },
  { label: "Oldest", sortBy: "created_at", sortOrder: "asc" },
  { label: "Highest score", sortBy: "score", sortOrder: "desc" },
  { label: "Lowest score", sortBy: "score", sortOrder: "asc" },
];

interface AssessmentTableItem {
  id: string;
  name: string;
  email: string;
  date: string;
  cv: boolean;
  score: number;
  status: string;
  decision: string;
  metricScores?: Record<string, number | null>;
}

interface AssessmentTableSectionProps {
  assessmentId?: string;
}

type CVDownloadScope = 'selected' | 'filtered';

const searchSchema = Yup.object().shape({
  search: Yup.string().trim(),
});

const DEFAULT_RANGE: [number, number] = [0, 100];

const AssessmentTableSection: React.FC<AssessmentTableSectionProps> = ({ assessmentId }) => {
  // Export modal
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportColumns, setExportColumns] = useState<string[]>(
    EXPORT_COLUMNS.map((c) => c.value)
  );
  const exportCandidates = useExportCandidates();
  const downloadCVs = useDownloadCVs();

  // Allow retake
  const candidateRetake = useCandidateRetake();
  const [pendingRetakeCandidateId, setPendingRetakeCandidateId] = useState<string | null>(null);

  // Fetch decision buckets from backend
  const { data: decisionBucketsData } = useFetchDecisionBuckets(assessmentId);
  const decisionBuckets = decisionBucketsData?.decision_buckets || [];
  
  // Map filter to decision value for API
  const getDecisionFromFilter = (filter: string): CandidateDecision | undefined => {
    if (filter === 'All') return undefined;
    const bucket = decisionBuckets.find(b => b.decision === filter);
    if (!bucket) return undefined;
    
    // Map backend decision names to API decision values
    const decisionMap: Record<string, CandidateDecision> = {
      'Interview': 'interview',
      'Revisit': 'revisit',
      'Probe further': 'probe_further',
      'Request More Info': 'request_more_info',
      'Save for future': 'save_for_future',
      'Reject': 'reject',
      'Rejected': 'reject',
    };
    return decisionMap[bucket.decision] || bucket.decision.toLowerCase().replace(' ', '_') as CandidateDecision;
  };
  
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get filters from URL or use defaults
  const filterFromUrl = searchParams.get("decision") || "All";
  const searchFromUrl = searchParams.get("search") || "";
  const pageFromUrl = parseInt(searchParams.get("page") || "1", 10);
  const minScoreFromUrl = parseInt(searchParams.get("minScore") || "0", 10);
  const maxScoreFromUrl = parseInt(searchParams.get("maxScore") || "100", 10);
  const sortByFromUrl = (searchParams.get("sortBy") as SortByField) || "created_at";
  const sortOrderFromUrl = (searchParams.get("sortOrder") as "asc" | "desc") || "desc";
  const metricsFromUrl = (searchParams.get("metrics") || "").split(",").filter(Boolean);

  const { register, formState: { errors }, watch } = useForm({
    resolver: yupResolver(searchSchema),
    mode: "onBlur",
    defaultValues: {
      search: searchFromUrl,
    },
  });

  const watchSearch = watch("search");
  
  const [currentFilter, setCurrentFilter] = useState<string>(filterFromUrl);
  const [searchTerm, setSearchTerm] = useState<string>(searchFromUrl);
  const [currentPage, setCurrentPage] = useState<number>(pageFromUrl);
  // Filter state is applied immediately so changing a popover control updates
  // the candidate query without a separate confirmation step.
  const [range, setRange] = useState([minScoreFromUrl, maxScoreFromUrl]);
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(metricsFromUrl);
  const [sortBy, setSortBy] = useState<SortByField>(sortByFromUrl);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(sortOrderFromUrl);

  // The two % boxes track their own text so a half-typed or cleared value
  // doesn't get snapped to a number mid-keystroke.
  const [rangeText, setRangeText] = useState<[string, string]>([
    String(minScoreFromUrl),
    String(maxScoreFromUrl),
  ]);
  const pageSize = 10;

  // Capture the 'from' param (manage-jobs URL with filters) once on mount.
  // This must be a ref so it survives URL syncs that recreate the params object.
  const fromParamRef = useRef(searchParams.get('from'));

  // Track if component has initialized to avoid overwriting URL on mount
  const hasInitialized = useRef(false);

  // Set initialization flag after mount
  useEffect(() => {
    hasInitialized.current = true;
  }, []);

  // Metric mapping is built dynamically after candidates are fetched

  // Fetch candidates with current filter, search, and pagination
  const { candidates, totalCount, totalPages, isLoading } = useReviewTalentsCandidates({
    assessmentId,
    decision: getDecisionFromFilter(currentFilter),
    search: searchTerm,
    minScore: range[0],
    maxScore: range[1],
    metrics: selectedMetrics,
    sortBy,
    sortOrder,
    page: currentPage,
    pageSize,
  });

  // Update decision hook
  const { updateDecision } = useUpdateCandidateDecision();

  // Metric options are read off the rows, so a filter that matches nothing would
  // empty the picker and strand the recruiter with no way to undo it. Hold on to
  // the last set that came back instead.
  const metricKeysSignature = getMetricKeys(candidates).join(',');
  const [metricOptions, setMetricOptions] = useState<string[]>([]);

  useEffect(() => {
    if (metricKeysSignature) setMetricOptions(metricKeysSignature.split(','));
  }, [metricKeysSignature]);

  // Transform API data to table format
  const filteredData: AssessmentTableItem[] = candidates.map((candidate, index) => {
    // Format date to MM/DD/YYYY
    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    };

    // Map bucket to decision display text
    const decisionMap: Record<string, string> = {
      'to_interview': 'Interview',
      'revisit': 'Revisit',
      'probe_further': 'Probe further',
      'request_more_info': 'Request More Info',
      'save_for_future': 'Save for future',
      'rejected': 'Rejected',
      'assessed': 'Assessed',
    };

    return {
      id: candidate.id.toString(),
      name: candidate.full_name,
      email: candidate.email,
      date: formatDate(candidate.created_at),
      cv: !!candidate.resume,
      cvUrl: candidate.resume,
      score: parseFloat(candidate.total_score),
      status: candidate.status,
      decision: decisionMap[candidate.bucket] || candidate.bucket,
      metricScores: Object.fromEntries(
        selectedMetrics.map((metric) => [metric, getMetricScore(candidate, metric)])
      ),
    };
  });
  
  const [mobileDecisionsOpen, setMobileDecisionsOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterContentEl, setFilterContentEl] = useState<HTMLDivElement | null>(null);

  const handleFilterOpenChange = (open: boolean) => {
    setFilterOpen(open);
    if (open) {
      // Discard any unfinished text left behind by an Escape/outside dismissal.
      setRangeText([String(range[0]), String(range[1])]);
    }
  };

  // When the filter popover is open and taller than the viewport, pad the body
  // so the page (not the popover) becomes scrollable — the popover is anchored
  // to its trigger, so page scroll reveals the clipped bottom.
  useEffect(() => {
    if (!filterOpen || !filterContentEl) {
      document.body.style.paddingBottom = '';
      return;
    }

    const updatePadding = () => {
      const rect = filterContentEl.getBoundingClientRect();
      const overflow = rect.bottom - window.innerHeight;
      document.body.style.paddingBottom = overflow > 0 ? `${overflow + 24}px` : '';
    };

    updatePadding();
    const ro = new ResizeObserver(updatePadding);
    ro.observe(filterContentEl);
    window.addEventListener('resize', updatePadding);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updatePadding);
      document.body.style.paddingBottom = '';
    };
  }, [filterOpen, filterContentEl]);
  const searchRef = useRef<HTMLDivElement>(null);

  const [checks, setChecks] = useState<boolean[]>(
    new Array(filteredData.length).fill(false)
  );
  const [checkAll, setCheckAll] = useState(false);

  useEffect(() => {
    setCheckAll(checks.every((item) => item));
  }, [checks]);

  useEffect(() => {
    setChecks(new Array(filteredData.length).fill(false));
    setCheckAll(false);
  }, [filteredData.length]);

  const checkedIds = filteredData
    .filter((_, index) => checks[index])
    .map(item => parseInt(item.id));

  const [copyChecks, setCopyChecks] = useState<boolean[]>(
    new Array(filteredData.length).fill(false)
  );
  const [copyCheckAll, setCopyCheckAll] = useState(false);

  useEffect(() => {
    setCopyCheckAll(copyChecks.length > 0 && copyChecks.every((item) => item));
  }, [copyChecks]);

  useEffect(() => {
    setCopyChecks(new Array(filteredData.length).fill(false));
    setCopyCheckAll(false);
  }, [filteredData.length]);

  const copyCheckedIds = filteredData
    .filter((_, index) => copyChecks[index])
    .map(item => item.id);

  // Sync URL params with current state (skip on initial mount)
  useEffect(() => {
    if (!hasInitialized.current) return;

    const params = new URLSearchParams();
    if (currentFilter !== "All") {
      params.set("decision", currentFilter);
    }
    if (searchTerm) {
      params.set("search", searchTerm);
    }
    params.set("page", currentPage.toString());
    if (range[0] !== 0) {
      params.set("minScore", range[0].toString());
    }
    if (range[1] !== 100) {
      params.set("maxScore", range[1].toString());
    }
    if (selectedMetrics.length > 0) {
      params.set("metrics", selectedMetrics.join(","));
    }
    if (sortBy !== "created_at") {
      params.set("sortBy", sortBy);
    }
    if (sortOrder !== "desc") {
      params.set("sortOrder", sortOrder);
    }
    // Preserve the manage-jobs return URL so breadcrumbs can navigate back correctly
    if (fromParamRef.current) {
      params.set("from", fromParamRef.current);
    }
    setSearchParams(params, { replace: true });
  }, [currentFilter, searchTerm, currentPage, range, selectedMetrics, sortBy, sortOrder, setSearchParams]);

  const handleFilter = (status: string) => {
    setCurrentFilter(status);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Watch for search input changes and update search term with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(watchSearch?.trim() || "");
      setCurrentPage(1); // Reset to first page when search changes
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [watchSearch]);

  const getStatusCount = (status: string): number => {
    // For "All", return total count
    if (status === "All") return totalCount;
    const bucket = decisionBuckets.find((item) => item.decision === status);
    return bucket?.count ?? 0;
  };

  // Handler function for decision updates
  const handleDecisionUpdate = (ids: number[], decision: string) => {
    if (ids.length === 0) return;
    
    // Map display decision to API decision value
    const decisionMap: Record<string, CandidateDecision> = {
      'Interview': 'interview',
      'Revisit': 'revisit',
      'Probe further': 'probe_further',
      'Request More Info': 'request_more_info',
      'Save for future': 'save_for_future',
      'Reject': 'reject',
      'Rejected': 'reject',
    };
    
    const apiDecision = decisionMap[decision] || decision.toLowerCase().replace(' ', '_') as CandidateDecision;
    updateDecision({ assessment_response_ids: ids, decision: apiDecision });
  };

  const handleDownloadSingleCV = async (id: string, cvUrl: string) => {
    if (!cvUrl) return;

    const candidate = candidates.find(c => c.id.toString() === id);
    const filename = candidate
      ? `${candidate.full_name.replace(/\s+/g, '_')}_Resume.pdf`
      : 'Resume.pdf';

    try {
      const response = await fetch(cvUrl);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch {
      // Fallback: open in same tab so browser's native download kicks in
      window.location.href = cvUrl;
    }
  };

  const handleDownloadCVs = (scope: CVDownloadScope) => {
    if (!assessmentId || downloadCVs.isPending) return;

    const baseDownloadParams = {
      assessment_id: assessmentId,
      decision: currentFilter === 'All' ? undefined : getDecisionFromFilter(currentFilter),
      search: searchTerm || undefined,
      min_score: range[0],
      max_score: range[1],
      metrics: selectedMetrics,
      sort_by: sortBy,
      sort_order: sortOrder,
    };

    if (scope === 'selected') {
      if (copyCheckedIds.length === 0) return;

      downloadCVs.mutate({
        ...baseDownloadParams,
        response_ids: copyCheckedIds.map(Number),
      });
      return;
    }

    // 'filtered' — zip every CV matching the active filters, across all pages.
    // To grab just the current page, the recruiter ticks the header select-all
    // checkbox (which fills copyCheckedIds) and uses the 'selected' scope.
    downloadCVs.mutate(baseDownloadParams);
  };

  const downloadOptions: DropdownMenuOption[] = [
    ...(copyCheckedIds.length > 0
      ? [{
          text: `Download ${copyCheckedIds.length} selected CV${copyCheckedIds.length === 1 ? '' : 's'}`,
          onClick: () => handleDownloadCVs('selected'),
          key: 'download-selected',
        }]
      : []),
    {
      text: 'Download all filtered CVs',
      onClick: () => handleDownloadCVs('filtered'),
      key: 'download-filtered',
    },
  ];

  const handleOpenRetakeModal = (candidateId: string) => {
    setPendingRetakeCandidateId(candidateId);
  };

  const handleCloseRetakeModal = () => {
    if (candidateRetake.isPending) return;
    setPendingRetakeCandidateId(null);
  };

  const handleConfirmRetake = () => {
    if (!pendingRetakeCandidateId) return;

    candidateRetake.mutate(
      { response_id: pendingRetakeCandidateId },
      {
        onSuccess: () => setPendingRetakeCandidateId(null),
      }
    );
  };

  const commitFilters = (nextRange: number[], nextMetrics: string[]) => {
    setRange(nextRange);
    setSelectedMetrics(nextMetrics);
    // The score range is scoped to the field named by sort_by, so the first
    // selected metric leads the sort. With no metric it applies to total score.
    const primaryMetric = nextMetrics[0];
    setSortBy((primaryMetric as SortByField) ?? 'score');
    setSortOrder('desc');
    setCurrentPage(1);
  };

  const updateRange = (next: number[]) => {
    setRangeText([String(next[0]), String(next[1])]);
    commitFilters(next, selectedMetrics);
  };

  // Typing in a % box: keep whatever was typed, but only move the slider once
  // the text reads as a number inside the other bound.
  const handleRangeTextChange = (index: 0 | 1, raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 3);
    setRangeText((prev) => (index === 0 ? [digits, prev[1]] : [prev[0], digits]));

    if (digits === '') return;
    const value = Math.min(100, parseInt(digits, 10));
    const next = index === 0
      ? [Math.min(value, range[1]), range[1]]
      : [range[0], Math.max(value, range[0])];
    commitFilters(next, selectedMetrics);
  };

  // Blur settles the box back onto the value the slider actually holds, which
  // also repairs an empty box or a bound typed past the other one.
  const handleRangeTextBlur = () => {
    setRangeText([String(range[0]), String(range[1])]);
  };

  const resetFilters = () => {
    setRangeText([String(DEFAULT_RANGE[0]), String(DEFAULT_RANGE[1])]);
    setRange([...DEFAULT_RANGE]);
    setSelectedMetrics([]);
    setSortBy('created_at');
    setSortOrder('desc');
    setCurrentPage(1);
    setFilterOpen(false);
  };

  // Toolbar sort dropdown: set both field and direction in one step.
  const handleSortChange = (newSortBy: SortByField, newSortOrder: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setCurrentPage(1);
  };

  const currentSortLabel =
    SORT_OPTIONS.find((opt) => opt.sortBy === sortBy && opt.sortOrder === sortOrder)?.label ?? 'Sort';

  // Generate options from decision buckets
  const options: DropdownMenuOption[] = decisionBuckets.map((bucket) => {
    const isReject = bucket.decision.toLowerCase().includes('reject');
    return {
      text: isReject ? <span className={`${styles.delete}`}>{bucket.decision}</span> : bucket.decision,
      onClick: () => handleDecisionUpdate(checkedIds, bucket.decision),
      key: bucket.decision.toLowerCase().replace(' ', '_'),
    };
  });

  const isSearchActive = !!watchSearch?.trim();

  return (
    <div className='md:py-5 py-0'>
      <div className="w-full overflow-x-auto">
        <div className={`gap-4 ${styles.header}`}>
          <h3 className={styles.headerText}>
            Talents <span className={styles.talentCount}>{totalCount}</span>
          </h3>
          <div className={`${styles.sortSection}`}>
            {/* Cancel — shown in selection mode when not searching */}
            {checkedIds.length > 0 && !isSearchActive && (
              <button
                className="flex items-center gap-2 h-[40px] px-4 py-2 rounded-lg border border-[#D0D5DD] bg-white text-[#344054] text-sm font-medium cursor-pointer hover:bg-[#F9FAFB] whitespace-nowrap"
                onClick={() => {
                  setChecks(new Array(filteredData.length).fill(false));
                  setCheckAll(false);
                }}
              >
                <CloseIcon className="w-4 h-4 [&>path]:stroke-[#344054]" />
                Cancel
              </button>
            )}

            {/* Filter popover — shown only when no selection and not searching */}
            {checkedIds.length === 0 && !isSearchActive && (
              <div className="w-fit">
                <Popover.Root open={filterOpen} onOpenChange={handleFilterOpenChange}>
                  <Popover.Trigger
                    className="flex items-center justify-center gap-2 h-[40px] px-4 py-2 rounded-lg border border-[#D0D5DD] bg-white cursor-pointer text-[#344054] text-sm font-medium hover:bg-[#F9FAFB]"
                  >
                    Filter
                    <FilterSearchIcon />
                  </Popover.Trigger>
                  <Popover.Portal>
                    <Popover.Content
                      ref={setFilterContentEl}
                      side="bottom"
                      align="end"
                      sideOffset={8}
                      avoidCollisions={false}
                      className="bg-white rounded-lg w-[300px] max-w-[calc(100vw-32px)] flex flex-col shadow-lg border border-[#EAECF0] outline-none z-[10000] overflow-hidden"
                    >
                      <div className="flex justify-between items-center gap-3 px-4 py-3 border-b border-[#F2F4F7]">
                        <h3 className="text-[17px] font-semibold text-[#1D2939]">Filter</h3>
                        <button
                          type="button"
                          aria-label="Close filter"
                          className="bg-transparent border-none p-0 cursor-pointer shrink-0 leading-none"
                          onClick={() => setFilterOpen(false)}
                        >
                          <CloseCircleGreyIcon className="w-5 h-5" />
                        </button>
                      </div>

                      <div className="flex flex-col gap-5 px-4 py-4">
                        <div className="flex flex-col gap-4">
                          <h4 className="text-[15px] font-semibold text-[#1D2939]">Select Range</h4>
                          <RangeSlider onChange={updateRange} value={range} />
                          <div className="flex items-center justify-between gap-2">
                            {([0, 1] as const).map((bound) => (
                              <div
                                key={bound}
                                className="flex items-center justify-center h-8 flex-1 min-w-0 rounded-[6px] border border-[#012169] bg-white focus-within:ring-2 focus-within:ring-[#01216933]"
                              >
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  aria-label={bound === 0 ? "Minimum score" : "Maximum score"}
                                  value={rangeText[bound]}
                                  onChange={(e) => handleRangeTextChange(bound, e.target.value)}
                                  onBlur={handleRangeTextBlur}
                                  className="w-[3ch] bg-transparent border-none outline-none p-0 text-right text-[13px] text-[#344054]"
                                />
                                <span className="text-[13px] text-[#344054]">%</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="h-px bg-[#EAECF0]" />

                        <div className="flex flex-col gap-4">
                          <div className="flex flex-col gap-1">
                            <h4 className="text-[15px] font-semibold text-[#1D2939]">Filter by Metric</h4>
                            <p className="text-[12px] leading-[16px] text-[#475467]">
                              Only candidates whose scores fall within the selected range for the
                              selected metrics will be displayed.
                            </p>
                          </div>
                          <div className="flex flex-col gap-1">
                            {metricOptions.map((metric) => (
                              <label
                                key={metric}
                                className="flex items-center gap-2 py-1 rounded cursor-pointer text-[14px] leading-[20px] text-[#012169]"
                              >
                                <input
                                  type="checkbox"
                                  className={`${styles.check} ${styles.metricCheck}`}
                                  checked={selectedMetrics.includes(metric)}
                                  onChange={(e) => {
                                    const nextMetrics = e.target.checked
                                      ? [...selectedMetrics, metric]
                                      : selectedMetrics.filter((value) => value !== metric);
                                    commitFilters(range, nextMetrics);
                                  }}
                                />
                                {formatMetricLabel(metric)}
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-start gap-3 px-4 py-3 border-t border-[#F2F4F7]">
                        <Button
                          type="outline"
                          onClick={resetFilters}
                          className="!w-auto !min-w-0 !h-9 !px-4 !rounded-[6px] !border !border-[#344054] !bg-white !text-[#475467] !text-[14px] !font-medium"
                        >
                          Reset
                        </Button>
                      </div>
                    </Popover.Content>
                  </Popover.Portal>
                </Popover.Root>
              </div>
            )}

            {/* Sort — shown only when no selection and not searching */}
            {checkedIds.length === 0 && !isSearchActive && (
              <DropdownMenu
                align="left"
                options={SORT_OPTIONS.map((opt) => {
                  const isActive = sortBy === opt.sortBy && sortOrder === opt.sortOrder;
                  return {
                    key: opt.label,
                    active: isActive,
                    text: (
                      <span className={isActive ? "text-[#012169] font-semibold" : "text-[#344054]"}>
                        {opt.label}
                      </span>
                    ),
                    onClick: () => handleSortChange(opt.sortBy, opt.sortOrder),
                  };
                })}
                trigger={
                  <button
                    type="button"
                    className="flex items-center justify-center gap-2 h-[40px] px-4 py-2 rounded-lg border border-[#D0D5DD] bg-white cursor-pointer text-[#344054] text-sm font-medium hover:bg-[#F9FAFB] whitespace-nowrap"
                  >
                    {currentSortLabel}
                    <ChevronRightIcon className="w-4 h-4 -rotate-90 [&>path]:stroke-[1.5px]" />
                  </button>
                }
              />
            )}

            {/* Search — always visible, expands when active */}
            <div
              ref={searchRef}
              className={`${isSearchActive ? 'flex-1' : ''} transition-all duration-200`}
            >
              <Input
                Icon={SearchIconTwo}
                iconClassName={styles.searchIcon}
                placeholder="Search"
                type="search"
                name="search"
                register={register}
                validatorMessage={errors.search?.message || ''}
                showRequired={false}
                parentClassName={`${styles.searchInputWrapper} ${isSearchActive ? 'w-full' : ''}`}
                className={styles.searchInput}
              />
            </div>

            {/* Export — hidden when search active */}
            {!isSearchActive && (
              <button
                onClick={() => setShowExportModal(true)}
                className={`border-[#012169] border-[1.5px] rounded-[4px] px-4 py-1.5 text-[#012169] flex items-center gap-2 font-medium`}
              >
                Export
                <ExportIcon className="w-5 h-5 text-[#012169]" />
              </button>
            )}

            {/* Move Selected to — shown in selection mode when not searching */}
            {checkedIds.length > 0 && !isSearchActive && (
              <DropdownMenu
                options={options}
                trigger={
                  <Button
                    type='outline'
                    onClick={() => { }}
                    className={`${styles.sortButton} ${styles.selectedButton}`}
                  >
                    Move Selected to
                    <ChevronRightIcon className={styles.chevron} />
                  </Button>
                }
              />
            )}

            {/* Download CVs — only shown once at least one CV is selected */}
            {copyCheckedIds.length > 0 && !isSearchActive && (
              <DropdownMenu
                align="left"
                options={downloadOptions}
                trigger={
                  <Button
                    type='outline'
                    disabled={downloadCVs.isPending}
                    className={`${styles.sortButton} ${styles.downloadButton}`}
                  >
                    {downloadCVs.isPending
                      ? 'Preparing zip…'
                      : copyCheckedIds.length > 0
                        ? `Download CVs (${copyCheckedIds.length} selected)`
                        : 'Download CVs'}
                    <DocumentDownload />
                  </Button>
                }
              />
            )}
          </div>
        </div>
      </div>

      {/* Mobile Decisions accordion */}
      <div className="sm:hidden mt-3 mb-4 relative">
        <button
          className="flex items-center gap-2 text-[#475467] font-medium text-base !bg-transparent w-fit"
          onClick={() => setMobileDecisionsOpen(!mobileDecisionsOpen)}
        >
          Decisions
          <ChevronRightIcon className={`w-4 h-4 transition-transform duration-200 [&>path]:stroke-[1px] ${mobileDecisionsOpen ? '-rotate-90' : 'rotate-90'}`} />
        </button>
        {mobileDecisionsOpen && (
          <div className="absolute left-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-[#EAECF0] py-1 min-w-[210px] z-[50]">
            <p
              onClick={() => { handleFilter("All"); setMobileDecisionsOpen(false); }}
              className={`px-4 py-2 text-sm cursor-pointer hover:bg-[#F9FAFB] ${currentFilter === "All" ? "text-[#012169] font-semibold" : "text-[#667085]"}`}
            >
              All ({getStatusCount("All")})
            </p>
            {decisionBuckets.map((bucket) => {
              const isReject = bucket.decision.toLowerCase().includes('reject');
              return (
                <p
                  key={bucket.id}
                  onClick={() => { handleFilter(bucket.decision); setMobileDecisionsOpen(false); }}
                  className={`px-4 py-2 text-sm cursor-pointer hover:bg-[#F9FAFB] ${
                    currentFilter === bucket.decision
                      ? "text-[#012169] font-semibold"
                      : isReject
                      ? "text-[#E53E3E]"
                      : "text-[#667085]"
                  }`}
                >
                  {bucket.decision} ({getStatusCount(bucket.decision)})
                </p>
              );
            })}
          </div>
        )}
      </div>

      <div className={styles.tableSection}>
        <div className={`md:block hidden ${styles.filterSection}`}>
          <p className={styles.filterTitle}>Decisions</p>
          <div className={styles.buttons}>
            <p
              onClick={() => handleFilter("All")}
              className={`${styles.filterButton} ${currentFilter === "All" ? styles.active : ""}`}
            >
              All <span className={styles.number}>({getStatusCount("All")})</span>
            </p>
            {decisionBuckets.map((bucket) => (
              <p
                key={bucket.id}
                onClick={() => handleFilter(bucket.decision)}
                className={`${styles.filterButton} ${currentFilter === bucket.decision ? styles.active : ""}`}
              >
                {bucket.decision} <span className={styles.number}>({getStatusCount(bucket.decision)})</span>
              </p>
            ))}
          </div>
        </div>
        <div className="w-full overflow-x-auto">
          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <Spinner />
            </div>
          ) : (
            <AssessmentTable
              tableBodyItems={filteredData}
              handleView={(id: string) => {
                if (!assessmentId) return;
                const reportUrl = Routes.report({ id: assessmentId, id2: id });
                // Full current review-talents URL (path + every active filter/page/sort param)
                // so the report can navigate back to this exact filtered state.
                const reviewSearch = searchParams.toString();
                const reviewReturnUrl = reviewSearch
                  ? `${Routes.reviewTalents(assessmentId)}?${reviewSearch}`
                  : Routes.reviewTalents(assessmentId);
                const params = new URLSearchParams();
                params.set("retReview", reviewReturnUrl);
                // Forward the manage-jobs return URL so "Manage Jobs" breadcrumb on the report page works
                if (fromParamRef.current) {
                  params.set("from", fromParamRef.current);
                }
                // CEO: open each report in a new tab so the filtered list stays put.
                window.open(`${reportUrl}?${params.toString()}`, "_blank", "noopener");
              }}
              currentFilter={currentFilter}
              selectedMetrics={selectedMetrics}
              pagination={{
                current: currentPage,
                total: totalPages,
                handleChange: (page: number) => setCurrentPage(page),
                count: totalCount,
                length: pageSize,
              }}
              checks={checks}
              handleCheck={({ index, value }) => {
                setChecks((prev) =>
                  prev.map((item, idx) => (idx === index ? value : item))
                );
              }}
              checkAll={checkAll}
              onCheckAllChange={() => {
                setCheckAll(!checkAll);
                setChecks((prev) => prev.map(() => !checkAll));
              }}
              copyChecks={copyChecks}
              handleCopyCheck={({ index, value }) => {
                setCopyChecks((prev) =>
                  prev.map((item, idx) => (idx === index ? value : item))
                );
              }}
              copyCheckAll={copyCheckAll}
              onCopyCheckAllChange={() => {
                setCopyCheckAll(!copyCheckAll);
                setCopyChecks((prev) => prev.map(() => !copyCheckAll));
              }}
              decisionBuckets={decisionBuckets}
              onDecisionChange={(candidateId, decision) => {
                handleDecisionUpdate([parseInt(candidateId)], decision);
              }}
              onDownloadCV={handleDownloadSingleCV}
              onAllowRetake={handleOpenRetakeModal}
              emptyState={
                filteredData.length === 0 ? (
                  <EmptyTable image={emptyReport2} text="No talents found" />
                ) : undefined
              }
            />
          )}
        </div>

      </div>

      <Modal
        position="centered"
        show={!!pendingRetakeCandidateId}
        close={handleCloseRetakeModal}
        contentClassName="w-[90%] !max-w-[518px] p-6 rounded-[12px] text-center relative"
      >
        <button
          type="button"
          aria-label="Close"
          className="absolute top-3 right-3 bg-transparent text-[#475467] border-none cursor-pointer p-1 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleCloseRetakeModal}
          disabled={candidateRetake.isPending}
        >
          <CloseCircleGreyIcon className="text-[#475467]" />
        </button>
        <div className="w-20 h-20 rounded-[12px] bg-[#FFF5E6] text-[#DB8400] flex items-center justify-center mx-auto mt-8 mb-6">
          <svg width="46" height="49" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
            <path d="M 8.7 4.2 L 15.3 4.2 Q 16.5 4.2 17.1 5.24 L 20.4 10.96 Q 21 12 20.4 13.04 L 17.1 18.76 Q 16.5 19.8 15.3 19.8 L 8.7 19.8 Q 7.5 19.8 6.9 18.76 L 3.6 13.04 Q 3 12 3.6 10.96 L 6.9 5.24 Q 7.5 4.2 8.7 4.2 Z" />
            <line x1="12" y1="9" x2="12" y2="12.5" />
            <line x1="12" y1="15" x2="12.01" y2="15" />
          </svg>
        </div>
        <h3 className="text-[22px] font-semibold text-[#101828] mb-2">
          Allow candidate to retake assessment?
        </h3>
        <p className="text-base text-[#475467] leading-[1.5] mb-6 lg:w-[75%] mx-auto">
          A new assessment link will be sent to the candidate. This current report will be removed and
          replaced after the new attempt.
        </p>
        <div className="flex gap-3 justify-end">
          <Button
            type="outline"
            onClick={handleCloseRetakeModal}
            disabled={candidateRetake.isPending}
            className="!border !border-[#D0D5DD] !px-6 !w-fit min-w-fit !text-[#344054] !bg-transparent !rounded-[8px] !h-11"
          >
            Cancel
          </Button>
          <Button
            type="fill"
            onClick={handleConfirmRetake}
            disabled={candidateRetake.isPending}
            className="!bg-[#012169] !px-6 !w-fit min-w-fit !border-none !text-white !rounded-[8px] !h-11"
          >
            {candidateRetake.isPending ? "Processing..." : "Allow Retake"}
          </Button>
        </div>
      </Modal>

      {/* Export modal */}
      {showExportModal && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl w-[520px] max-w-[90vw] max-h-[90vh] shadow-xl overflow-hidden flex flex-col">
            <div className="flex justify-between items-center px-6 py-5 border-b border-[#EAECF0] shrink-0">
              <h3 className="text-xl font-bold text-[#1D2939]">Export Talents</h3>
              <button
                onClick={() => setShowExportModal(false)}
                className="p-1 rounded-full hover:bg-[#F2F4F7] cursor-pointer border-none bg-transparent"
              >
                <CloseCircleGreyIcon className="w-5 h-5 [&>path]:stroke-[#667085]" />
              </button>
            </div>
            <div className="px-6 py-6 overflow-y-auto">
              <p className="text-sm text-[#667085] mb-5">
                Select the columns you want to include in your export.
              </p>
              <div className="grid sm:grid-cols-2 gap-3 mb-6">
                {EXPORT_COLUMNS.map((col) => (
                  <label
                    key={col.value}
                    className="flex items-center gap-3 cursor-pointer text-sm text-[#344054] font-medium bg-[#F9FAFB] border border-[#EAECF0] rounded-md px-4 py-4"
                  >
                    <input
                      type="checkbox"
                      className="appearance-none shrink-0 w-5 h-5 rounded-[6px] border border-[#D0D5DD] bg-white cursor-pointer relative checked:bg-[#EFF8FF] checked:border-[1.5px] checked:border-[#012169] checked:after:content-[''] checked:after:absolute checked:after:top-[2.5px] checked:after:left-[5.5px] checked:after:w-[6px] checked:after:h-[10px] checked:after:border-r-2 checked:after:border-b-2 checked:after:border-[#012169] checked:after:rotate-[40deg]"
                      checked={exportColumns.includes(col.value)}
                      onChange={(e) => {
                        setExportColumns((prev) =>
                          e.target.checked
                            ? [...prev, col.value]
                            : prev.filter((c) => c !== col.value)
                        );
                      }}
                    />
                    {col.label}
                  </label>
                ))}
              </div>
              <div className="flex justify-end">
                <Button
                  type="fill"
                  onClick={() => {
                    if (!assessmentId) return;
                    exportCandidates.mutate({
                      assessment_id: assessmentId,
                      columns: exportColumns,
                      ...(checkedIds.length > 0 && { response_ids: checkedIds }),
                    });
                    setShowExportModal(false);
                  }}
                  className="!w-fit !px-6 !bg-[#012169] !text-white !border-none !rounded-[8px] !h-11"
                  disabled={exportColumns.length === 0}
                >
                  {exportCandidates.isPending ? 'Exporting…' : 'Export CSV'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export { AssessmentTableSection };
